import React, { useEffect, useState } from "react";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import { PayShareType } from "../types/payShareTypes";
import { useFeeType } from "../../feetypes/hooks/useFeeType";
import { useStudent } from "../../peoples/students/hooks/useStudent";
import { usePaySharePayment } from "../hooks/usePaySharePayment";
import { useSelector } from "react-redux";
import { selectUserRole } from "../../../core/data/slice/authSlice";

// Update PayShareType to include share_type
type PaymentStatus = "pending" | "paid";

// Define invoice type to avoid 'any' implications
interface Invoice {
  id: number;
  type: string;
  payable: number;
  status: string;
  student_id?: number;
  course_id?: number;
  stage_id?: number | null;
  [key: string]: any; // For other properties we might not know
}

const PayShareDetails = () => {
  const routes = all_routes;
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [payShare, setPayShare] = useState<PayShareType | null>(null);
  const navigate = useNavigate();

  // Get student data from the same hook used in the list component
  const { getStudentState } = useStudent();
  const { students, isLoading: isStudentsLoading } = getStudentState();

  // Get fee types to identify fee type names
  const { getFeeTypeState } = useFeeType({ size: 100 });
  const { feetypes, isLoading: isFeeTypesLoading } = getFeeTypeState();

  // Add this near other hooks
  const { initiatePayment, isProcessing } = usePaySharePayment(() => { });

  const userRole = useSelector(selectUserRole);
  const isSuperAdmin = userRole === "superadmin";

  // console.log(payShare, 'ps---')

  // Process data to create a PayShare object
  useEffect(() => {
    // First check if we have payShare data passed via location state
    if (location.state?.payShare) {
      // Add center_id and session_id to the payShare object from location state
      const enrichedPayShare = {
        ...location.state.payShare,
        center_id: location.state.payShare.center_id,
        session_id: location.state.payShare.session_id,
        course_id: location.state.payShare.course_id
      };
      setPayShare(enrichedPayShare);
      return;
    }

    // If not, find the student from the list
    if (students && students.length > 0 && id) {
      const student = students.find((s) => s.id === Number(id));

      if (student) {
        console.log("Student Data from list:", {
          id: student.id,
          name: student.name || "",
          center: student.center,
          invoices: student.invoices,
          centerPayments: student.centerPayments,
        });

        // Find the invoice - get the first invoice that matches either registration or course fee
        let invoice: Invoice | null = null;
        let feeTypeName = "Unknown";

        if (student.invoices && student.invoices.length > 0) {
          // First try to find a registration fee invoice
          invoice =
            student.invoices.find(
              (inv: any) => inv.type === "registrationfee"
            ) || null;

          if (invoice) {
            feeTypeName = "Registration Fee";
          } else {
            // If no registration fee, look for course fee
            invoice =
              student.invoices.find((inv: any) => inv.type === "coursefee") ||
              null;
            if (invoice) {
              feeTypeName = "Course Fee";
            }
          }

          // Log invoice details
          // console.log("Selected Invoice:", invoice);
        }

        if (invoice) {
          // Get fee info from center data
          let feeAmount = invoice.payable || 0;
          let shareAmount = 0;
          let shareType: "fixed" | "percent" = "fixed";

          // For registration fee
          if (
            invoice.type === "registrationfee" &&
            student.center?.reg_fee_share &&
            student.center.reg_fee_share.length > 0
          ) {
            const regFeeShare = student.center.reg_fee_share.find(
              (fee: any) => fee.course_id === student.course_id
            );

            if (regFeeShare) {
              // Use reg_fee as the fee amount
              feeAmount = regFeeShare.reg_fee || feeAmount;
              shareType =
                regFeeShare.share_type === "percent" ? "percent" : "fixed";

              console.log("Registration Fee Share:", {
                feeAmount,
                shareType,
                shareAmount: regFeeShare.share_amount,
                sharePercent:
                  shareType === "percent" ? regFeeShare.share_amount : null,
              });

              if (shareType === "fixed") {
                shareAmount = regFeeShare.share_amount || 0;
              } else if (shareType === "percent") {
                // Calculate percentage of the fee amount
                shareAmount = Math.round(
                  feeAmount * (regFeeShare.share_amount / 100)
                );
              }
            }
          }
          // For course fee
          else if (
            invoice.type === "coursefee" &&
            student.center?.course_fee_share &&
            student.center.course_fee_share.length > 0
          ) {
            // Find the course fee share for the student's course
            const courseFeeShare = student.center.course_fee_share.find(
              (fee: any) => fee.course_id === student.course_id
            );

            if (courseFeeShare) {
              // Look for stageFeeShare array (it might be named differently in the API response)
              const stageFeeShareArray =
                courseFeeShare.stageFeeShare || courseFeeShare.stage_fee_share;

              if (stageFeeShareArray && Array.isArray(stageFeeShareArray)) {
                // Find the stage fee share for the student's stage
                const stageFeeShare = stageFeeShareArray.find(
                  (stage: any) => stage.stage_id === student.stage_id
                );

                if (stageFeeShare) {
                  // Use stage_fee as the fee amount if available
                  feeAmount = stageFeeShare.stage_fee || feeAmount;
                  shareType =
                    courseFeeShare.share_type === "percent"
                      ? "percent"
                      : "fixed";

                  console.log("Course Fee Share:", {
                    feeAmount,
                    shareType,
                    stageId: stageFeeShare.stage_id,
                    stageName: stageFeeShare.stage_name,
                    shareAmount: stageFeeShare.share_amount,
                    sharePercent:
                      shareType === "percent"
                        ? stageFeeShare.share_amount
                        : null,
                  });

                  if (shareType === "fixed") {
                    shareAmount = stageFeeShare.share_amount || 0;
                  } else if (shareType === "percent") {
                    // Calculate percentage of the fee amount
                    shareAmount = Math.round(
                      feeAmount * (stageFeeShare.share_amount / 100)
                    );
                  }
                }
              }
            }
          }

          // Check payment status from centerPayments
          const paymentStatus = student.centerPayments?.find(
            (payment: any) =>
              payment.fee_type === (invoice?.type === "coursefee" ? "coursefee" : "registrationfee") &&
              (invoice?.type === "registrationfee" || payment.stage_id === student.course?.stage?.id)
          )?.status === "completed" ? "paid" : "pending";

          const paymentDate = student.centerPayments?.find(
            (payment: any) =>
              payment.fee_type === (invoice?.type === "coursefee" ? "coursefee" : "registrationfee") &&
              (invoice?.type === "registrationfee" || payment.stage_id === student.course?.stage?.id)
          )?.created_at?.split("T")[0] || new Date().toISOString().split("T")[0];

          // Set fee type name based on invoice type
          const feeTypeName = invoice.type === "coursefee"
            ? "Course Fee"
            : "Registration Fee";

          // Create PayShare object
          const payShareObject: PayShareType = {
            id: student.id,
            student_id: student.profile?.studentid || `STU${student.id}`,
            student_name: student.name || "",
            session: student.session?.name || "-",
            course: student.course?.name || "-",
            center_name: student.center?.name || "-",
            fee_type: feeTypeName,
            stage: student.course?.stage?.name || "-",
            amount: feeAmount,
            share_amount: shareAmount,
            share_type: shareType,
            payment_date: paymentDate,
            payment_status: paymentStatus,
          };

          console.log("PayShare Object:", payShareObject);
          setPayShare(payShareObject);
        } else {
          console.error("No invoice found for student:", student.id);
        }
      }
    }
  }, [students, id, location.state]);

  // Calculate share percentage
  const calculateSharePercentage = () => {
    if (!payShare || payShare.amount <= 0) return 0;
    return ((payShare.share_amount / payShare.amount) * 100).toFixed(2);
  };

  // Add this function before the return statement
  const handlePayShare = async () => {
    if (!payShare) return;

    try {
      const payShareArray = [{
        ...payShare,
        session_id: Number(payShare.session_id || 0),
        course_id: Number(payShare.course_id || 0),
        stage_id: Number(payShare.stage_id || 0)
      }];

      const feeTypeCode = payShare.fee_type.toLowerCase().includes('course') ? 'coursefee' : 'registrationfee';

      await initiatePayment(payShareArray, feeTypeCode);

      // Navigate back to list after successful payment
      navigate(routes.payShareList);
    } catch (error) {
      console.error('Payment initiation failed:', error);
    }
  };

  if (isStudentsLoading || isFeeTypesLoading) {
    return (
      <div className="page-wrapper">
        <div className="content container">
          <div className="row justify-content-center">
            <div className="col-md-8">
              <div className="card">
                <div className="card-body text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3">Loading Claim details...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!payShare) {
    return (
      <div className="page-wrapper">
        <div className="content container">
          <div className="row justify-content-center">
            <div className="col-md-8">
              <div className="card">
                <div className="card-body text-center py-5">
                  <i className="ti ti-alert-circle text-warning fs-1 mb-3"></i>
                  <h4>Claim Not Found</h4>
                  <p>
                    The claim record you're looking for doesn't exist or has
                    been removed.
                  </p>
                  <Link
                    to={routes.payShareList}
                    className="btn btn-primary mt-3"
                    onClick={() => {
                      // Don't need to do anything special - filters are already preserved in session storage
                    }}
                  >
                    <i className="ti ti-arrow-left me-2"></i>
                    Back to Claims List
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content container">
        {/* Breadcrumb */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Claims Details</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.centerDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.payShareList}>Claims</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Claims Details
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <div className="mb-2">
              <Link
                to={routes.payShareList}
                className="btn btn-outline-primary d-flex align-items-center"
                onClick={() => {
                  // Don't need to do anything special - filters are already preserved in session storage
                }}
              >
                <i className="ti ti-arrow-left me-2"></i>
                Back
              </Link>
            </div>
          </div>
        </div>

        <div className="row">
          {/* Main Content - Student Information */}
          <div className="col-md-8 mb-4">
            <div className="card">
              <div className="card-header bg-light">
                <div className="d-flex align-items-center">
                  <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                    <i className="ti ti-coin fs-16"></i>
                  </span>
                  <h4 className="text-dark mb-0">Claims Details</h4>
                </div>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <span
                      className={`badge badge-soft-${payShare.payment_status === "paid"
                        ? "success"
                        : "warning"
                        } d-inline-flex align-items-center`}
                    >
                      <i className="ti ti-circle-filled fs-5 me-1"></i>
                      {payShare.payment_status.charAt(0).toUpperCase() +
                        payShare.payment_status.slice(1)}
                    </span>
                    {/* <span className="text-muted">|</span>
                    <span className="text-muted">
                      Date: {payShare.payment_date}
                    </span> */}
                  </div>
                </div>

                {/* Add alert for pending status */}
                {payShare.payment_status === "pending" && (
                  <div className="alert alert-warning mb-3" role="alert">
                    <div className="d-flex">
                      <i className="ti ti-alert-triangle mt-2 me-2"></i>
                      <div>
                        <h6 className="alert-heading mb-1">Pending Payment</h6>
                        <p className="mb-0">
                          This claim is pending payment. The share amount
                          needs to be paid to the main center.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Student Information */}
                <div className="mb-4">
                  <h5 className="border-bottom pb-2 mb-3">
                    Student Information
                  </h5>
                  <div className="row mb-3">
                    <div className="col-sm-4">
                      <p className="text-muted mb-0">Student ID:</p>
                    </div>
                    <div className="col-sm-8">
                      <p className="mb-0 fw-medium">{payShare.student_id}</p>
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-sm-4">
                      <p className="text-muted mb-0">Student Name:</p>
                    </div>
                    <div className="col-sm-8">
                      <p className="mb-0 fw-medium">{payShare.student_name}</p>
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-sm-4">
                      <p className="text-muted mb-0">Session:</p>
                    </div>
                    <div className="col-sm-8">
                      <p className="mb-0 fw-medium">{payShare.session}</p>
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-sm-4">
                      <p className="text-muted mb-0">Course:</p>
                    </div>
                    <div className="col-sm-8">
                      <p className="mb-0 fw-medium">{payShare.course}</p>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-sm-4">
                      <p className="text-muted mb-0">Center:</p>
                    </div>
                    <div className="col-sm-8">
                      <p className="mb-0 fw-medium">{payShare.center_name}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Info - Sidebar */}
          <div className="col-md-4 mb-4">
            <div className="card h-100">
              <div className="card-header bg-light">
                <div className="d-flex align-items-center">
                  <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                    <i className="ti ti-cash fs-16"></i>
                  </span>
                  <h4 className="text-dark mb-0">Payment Information</h4>
                </div>
              </div>
              <div className="card-body">
                <div className="row mb-3">
                  <div className="col-6">
                    <p className="text-muted mb-0">Fee Type:</p>
                  </div>
                  <div className="col-6">
                    <p className="mb-0 fw-medium">{payShare.fee_type === "coursefee" ? "Course Fee" : "Registration Fee"}</p>
                  </div>
                </div>
                {payShare.fee_type === "Course Fee" && (
                  <div className="row mb-3">
                    <div className="col-6">
                      <p className="text-muted mb-0">Level:</p>
                    </div>
                    <div className="col-6">
                      <p className="mb-0 fw-medium">{payShare.stage}</p>
                    </div>
                  </div>
                )}
                <div className="row mb-3">
                  <div className="col-6">
                    <p className="text-muted mb-0">Amount:</p>
                  </div>
                  <div className="col-6">
                    <p className="mb-0 fw-medium">
                      {payShare.amount > 0 ? (
                        `₹${payShare.amount.toLocaleString()}`
                      ) : (
                        <span className="text-muted fst-italic fs-12">
                          No fee available
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-6">
                    <p className="text-muted mb-0">Share Amount:</p>
                  </div>
                  <div className="col-6">
                    <p className="mb-0 fw-medium">
                      {payShare.share_amount > 0 ? (
                        `₹${payShare.share_amount.toLocaleString()}`
                      ) : (
                        <span className="text-muted fst-italic fs-12">
                          No fee available
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-6">
                    <p className="text-muted mb-0">Share Percentage:</p>
                  </div>
                  <div className="col-6">
                    <p className="mb-0 fw-medium">
                      {calculateSharePercentage()}%
                    </p>
                  </div>
                </div>

                <div className="row">
                  <div className="col-6">
                    <p className="text-muted mb-0">Status:</p>
                  </div>
                  <div className="col-6">
                    <span
                      className={`badge badge-soft-${payShare.payment_status === "paid"
                        ? "success"
                        : "warning"
                        } d-inline-flex align-items-center`}
                    >
                      <i className="ti ti-circle-filled fs-5 me-1"></i>
                      {payShare.payment_status.charAt(0).toUpperCase() +
                        payShare.payment_status.slice(1)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Details and Actions - in a row at the bottom */}
          <div className="row">
            {/* Payment Details */}
            <div className="col-md-8 mb-4">
              <div className="card h-100">
                <div className="card-header bg-light">
                  <div className="d-flex align-items-center">
                    <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                      <i className="ti ti-list-details fs-16"></i>
                    </span>
                    <h4 className="text-dark mb-0">Payment Details</h4>
                  </div>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-bordered">
                      <thead className="table-light">
                        <tr>
                          <th>Total Amount</th>
                          <th>Share Amount</th>

                          <th>Share Percentage</th>
                          {payShare.fee_type === "Course Fee" && <th>Level</th>}
                          <th>Payment Date</th>
                          <th>Payment Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            {payShare.amount > 0 ? (
                              `₹${payShare.amount.toLocaleString()}`
                            ) : (
                              <span className="text-muted fst-italic fs-12">
                                No fee available
                              </span>
                            )}
                          </td>
                          <td>
                            {payShare.share_amount > 0 ? (
                              `₹${payShare.share_amount.toLocaleString()}`
                            ) : (
                              <span className="text-muted fst-italic fs-12">
                                No fee available
                              </span>
                            )}
                          </td>

                          <td>{calculateSharePercentage()}%</td>
                          {payShare.fee_type === "Course Fee" && (
                            <td>{payShare.stage}</td>
                          )}
                          <td>{payShare.payment_date}</td>
                          <td>
                            <span
                              className={`badge badge-soft-${payShare.payment_status === "paid"
                                ? "success"
                                : "warning"
                                } d-inline-flex align-items-center`}
                            >
                              <i className="ti ti-circle-filled fs-5 me-1"></i>
                              {payShare.payment_status.charAt(0).toUpperCase() +
                                payShare.payment_status.slice(1)}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions Card - Only show for non-superadmin users */}
            {!isSuperAdmin && payShare.payment_status === "pending" && (
              <div className="col-md-4 mb-4">
                <div className="card h-100">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-cash-banknote fs-16"></i>
                      </span>
                      <h4 className="text-dark mb-0">Actions</h4>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="d-flex flex-column gap-3">
                      <button
                        className="btn btn-outline-primary"
                        disabled={payShare.amount <= 0 || isProcessing}
                        onClick={handlePayShare}
                      >
                        {isProcessing ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Processing...
                          </>
                        ) : (
                          <>
                            <i className="ti ti-cash-banknote me-2"></i>
                            Pay Claim
                          </>
                        )}
                      </button>
                      {payShare.amount <= 0 && (
                        <small className="text-warning">
                          <i className="ti ti-alert-circle me-1"></i>
                          Generate invoices first to enable payment
                        </small>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayShareDetails;
